import { BlackPopupRolagem } from "@/components/blog/BlackPopupRolagem";
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

import { SiteHeader } from "@/components/feo/SiteHeader";
import { BlackDestaqueSection } from "@/components/black2x1/BlackDestaqueSection";
import { SiteFooter } from "@/components/feo/SiteFooter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PROFESSORES_BY_PRODUCT } from "@/components/posfigma/professores-by-product";
import { POS_THEMES } from "@/components/posfigma/theme";
import type { ProductSlug } from "@/components/checkout/PreCheckoutDialog";

export const Route = createFileRoute("/professores")({
  head: () => {
    const seen = new Set<string>();
    const persons: { nome: string; foto: string }[] = [];
    for (const product of ["orto", "dor", "esporte", "geronto", "trauma", "prescricao"] as const) {
      for (const p of PROFESSORES_BY_PRODUCT[product]) {
        if (seen.has(p.nome)) continue;
        seen.add(p.nome);
        persons.push({ nome: p.nome, foto: p.foto });
        if (persons.length >= 20) break;
      }
      if (persons.length >= 20) break;
    }
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Corpo Docente FEO",
      url: "https://fisioemortopedia.com.br/professores",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: persons.map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          item: {
            "@type": "Person",
            name: p.nome,
            image: p.foto,
            jobTitle: "Professor(a) da FEO",
            worksFor: {
              "@type": "EducationalOrganization",
              name: "Fisio em Ortopedia",
              url: "https://fisioemortopedia.com.br",
            },
          },
        })),
      },
    };
    return {
      meta: [
        { title: "Corpo Docente | Fisio em Ortopedia" },
        {
          name: "description",
          content:
            "Conheça o corpo docente FEO: mais de 80 especialistas, mestres e doutores em fisioterapia musculoesquelética, dor, esporte, gerontologia e traumato-ortopedia.",
        },
        { property: "og:title", content: "Corpo Docente FEO" },
        {
          property: "og:description",
          content:
            "Mais de 80 professores especialistas, mestres e doutores em fisioterapia musculoesquelética. Veja em qual pós cada um dá aula.",
        },
        { property: "og:url", content: "https://fisioemortopedia.com.br/professores" },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: "Corpo Docente FEO" },
        { name: "twitter:description", content: "Mais de 80 professores referência em fisioterapia musculoesquelética." },
      ],
      links: [{ rel: "canonical", href: "https://fisioemortopedia.com.br/professores" }],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }],
    };
  },
  component: ProfessoresPage,
});



const PRODUCT_ORDER: ProductSlug[] = [
  "orto",
  "dor",
  "esporte",
  "geronto",
  "trauma",
  "prescricao",
];

const PRODUCT_LABEL: Record<ProductSlug, string> = {
  orto: "Orto",
  dor: "Dor",
  esporte: "Esporte",
  geronto: "Geronto",
  trauma: "Trauma",
  prescricao: "Prescrição",
};

const PRODUCT_FULL: Record<ProductSlug, string> = {
  orto: "Pós Orto",
  dor: "Pós Dor",
  esporte: "Pós Esporte",
  geronto: "Pós Gerontologia",
  trauma: "Pós Traumato",
  prescricao: "Pós Prescrição",
};

const PRODUCT_DOT: Record<ProductSlug, string> = {
  orto: "#00C9A7",
  dor: "#7C7FFF",
  esporte: "#D63A48",
  geronto: "#9B5BFE",
  trauma: "#C6B077",
  prescricao: "#6788A8",
};

type Aggregated = {
  nome: string;
  foto: string;
  products: ProductSlug[];
};

function normalize(s: string) {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function buildAggregate(): Aggregated[] {
  const map = new Map<string, Aggregated>();
  for (const product of PRODUCT_ORDER) {
    for (const p of PROFESSORES_BY_PRODUCT[product]) {
      const key = normalize(p.nome);
      const existing = map.get(key);
      if (existing) {
        if (!existing.products.includes(product)) existing.products.push(product);
      } else {
        map.set(key, { nome: p.nome, foto: p.foto, products: [product] });
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}

function ProfessoresPage() {
  const all = useMemo(buildAggregate, []);
  const [filter, setFilter] = useState<"all" | ProductSlug>("all");

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: all.length };
    for (const product of PRODUCT_ORDER) {
      c[product] = all.filter((p) => p.products.includes(product)).length;
    }
    return c;
  }, [all]);

  const filtered = useMemo(() => {
    return all.filter((p) => {
      if (filter !== "all" && !p.products.includes(filter)) return false;
      return true;
    });
  }, [all, filter]);

  return (
    <div className="min-h-screen bg-white text-[#13123a]">
      <SiteHeader />

      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <p className="text-[var(--brand-soft)] font-semibold uppercase tracking-widest text-xs mb-3">
            Corpo docente
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight max-w-3xl">
            Nossos <span className="text-[#5a62ff]">professores</span>
          </h1>
          <p className="mt-6 text-[#13123a]/75 text-base sm:text-lg leading-relaxed max-w-3xl">
            Mais de 80 professores especialistas, mestres e doutores em fisioterapia
            musculoesquelética, todos referência clínica e científica nas suas áreas.
          </p>
        </div>
      </section>

      <section className="pb-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 className="sr-only">Lista de professores</h2>
          <div className="flex flex-wrap gap-2 mb-8">
            <Button
              size="sm"
              onClick={() => setFilter("all")}
              className={
                "bg-[#13123a] border border-[#13123a] hover:bg-[#13123a] hover:text-[#95a2ff] " +
                (filter === "all" ? "text-[#95a2ff]" : "text-white")
              }
            >
              Todos <span className="ml-2 opacity-80">{counts.all}</span>
            </Button>
            {PRODUCT_ORDER.map((p) => (
              <Button
                key={p}
                size="sm"
                onClick={() => setFilter(p)}
                className={
                  "bg-[#13123a] border border-[#13123a] hover:bg-[#13123a] hover:text-[#95a2ff] " +
                  (filter === p ? "text-[#95a2ff]" : "text-white")
                }
              >
                <span className="mr-2 inline-block h-2 w-2 rounded-full" style={{ background: PRODUCT_DOT[p] }} />
                {PRODUCT_FULL[p]} <span className="ml-2 opacity-80">{counts[p]}</span>
              </Button>
            ))}
          </div>



          <p className="text-xs text-[#13123a]/60 mb-4">
            Mostrando {filtered.length} de {all.length} professores.
          </p>

          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#13123a]/15 py-16 text-center text-[#13123a]/60">
              Nenhum professor encontrado com esses filtros.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {filtered.map((p) => (
                <article
                  key={p.nome}
                  className="rounded-2xl bg-white border border-[#13123a]/10 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="aspect-square overflow-hidden bg-[#13123a]/5">
                    <img
                      src={p.foto}
                      alt={`Foto de ${p.nome}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="font-bold text-sm text-[#13123a] leading-tight">{p.nome}</h3>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {p.products.map((prod) => (
                      <Badge
                        key={prod}
                        variant="secondary"
                        className="text-[10px] px-1.5 py-0 bg-[#13123a]/5 text-[#13123a]/80 hover:bg-[#13123a]/10"
                      >
                        <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full" style={{ background: PRODUCT_DOT[prod] }} />
                        {PRODUCT_LABEL[prod]}
                      </Badge>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white border-t border-[#13123a]/10">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#13123a]">
            Quer aprender com <span className="text-[#5a62ff]">eles</span>?
          </h2>
          <p className="mt-4 text-[#13123a]/75 text-base sm:text-lg leading-relaxed">
            Nossas pós-graduações 100% online são reconhecidas pelo MEC e ministradas
            por este corpo docente. Conheça os programas disponíveis.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <Button
              asChild
              className="bg-gradient-to-r from-[#95a2ff] to-[#7C7FFF] text-[#13123a] border-transparent hover:from-[#7C7FFF] hover:to-[#95a2ff] shadow-lg shadow-[#95a2ff]/30 font-bold transition-transform hover:scale-105"
            >
              <Link to="/pos">
                Ver pós-graduações
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <BlackDestaqueSection origem="professores" />

      <BlackPopupRolagem origem="professores" />
      <SiteFooter />
    </div>
  );
}
